import React, { useState, useEffect, memo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo } from '@fortawesome/free-solid-svg-icons';

interface MeetingBannerProps {
  targetDate: Date | string;
  meetingUrl: string;
  title?: string;
  onJoinClick?: () => void;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

// --- Helper: Formats numbers to 2 digits ---
const formatValue = (val: number) => (val < 10 ? `0${val}` : val.toString());

// --- 1. Fixed Flip Digit Component ---
// Defined OUTSIDE the main component so 'memo' works correctly.
const FlipDigit = memo(({ value, label }: { value: number; label?: string }) => {
  // We track the previous value to simulate the "Old Number" flipping down to reveal the "New Number"
  const prevValueRef = useRef(value);
  
  // Update ref only after render logic is set up
  if (prevValueRef.current !== value) {
    // We intentionally don't update current here immediately to allow the render of the 'old' value in the flap
    // The visual update happens via the key change on the animation div
  }

  const currentStr = formatValue(value);
  const prevStr = formatValue(prevValueRef.current);

  // Sync ref after render for next time
  useEffect(() => {
    prevValueRef.current = value;
  }, [value]);

  return (
    <div className="flex flex-col items-center mx-[2px]">
      <div className="relative w-8 h-10 bg-[#5e35b1] rounded-md shadow-sm border-b border-black/20 group perspective-container">
        
        {/* STATIC LAYER (Background): Shows the NEW (Current) value. 
            This is what is revealed when the flap falls down. */}
        <div className="absolute inset-0 flex flex-col">
            {/* Top Half */}
            <div className="relative h-1/2 w-full overflow-hidden bg-[#673ab7] z-0 flex items-end justify-center rounded-t-md">
                <span className="text-white font-mono font-bold text-lg translate-y-[50%] leading-none">{currentStr}</span>
            </div>
            {/* Bottom Half */}
            <div className="relative h-1/2 w-full overflow-hidden bg-[#5e35b1] z-0 flex items-start justify-center rounded-b-md">
                <span className="text-white font-mono font-bold text-lg -translate-y-[50%] leading-none">{currentStr}</span>
            </div>
        </div>

        {/* ANIMATION LAYER (The Flap): Shows the OLD (Previous) value.
            When value changes, this sits on top and flips down. */}
        <div key={value} className="absolute inset-0 z-10 animate-flip origin-center transform-style-3d">
            {/* Front Face: The Old Top Half */}
            <div className="absolute top-0 left-0 right-0 h-1/2 overflow-hidden bg-[#673ab7] backface-hidden flex items-end justify-center rounded-t-md border-b border-black/10">
                 <span className="text-white font-mono font-bold text-lg translate-y-[50%] leading-none">{prevStr}</span>
            </div>
            
            {/* Back Face: The New Bottom Half (Visible when flipped 180deg) */}
            <div className="absolute top-0 left-0 right-0 h-1/2 overflow-hidden bg-[#5e35b1] backface-hidden rotate-x-180 flex items-start justify-center rounded-b-md">
                 <span className="text-white font-mono font-bold text-lg -translate-y-[50%] leading-none">{currentStr}</span>
            </div>
        </div>
        
        {/* Split Line */}
        <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-black/20 z-20"></div>
      </div>
      
      {label && <span className="text-[10px] uppercase font-bold tracking-wider opacity-80 mt-1 text-white">{label}</span>}
      
      <style>{`
        .perspective-container {
            perspective: 300px;
        }
        .transform-style-3d {
            transform-style: preserve-3d;
        }
        .backface-hidden {
            backface-visibility: hidden;
        }
        .rotate-x-180 {
            transform: rotateX(180deg);
        }
        .animate-flip {
          animation: flipDown 0.6s ease-in forwards;
        }
        @keyframes flipDown {
          0% { transform: rotateX(0deg); }
          100% { transform: rotateX(-180deg); }
        }
      `}</style>
    </div>
  );
});

// --- 2. Main Banner Component ---
const MeetingBanner: React.FC<MeetingBannerProps> = ({ 
  targetDate, 
  meetingUrl, 
  title = "Team Standup",
  onJoinClick
}) => {
  const PRE_JOIN_WINDOW = 30 * 60 * 1000; 
  
  const calculateTimeLeft = (): TimeLeft => {
    const difference = +new Date(targetDate) - +new Date();
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      totalMs: difference,
    };
  };

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft());
  const [canJoin, setCanJoin] = useState(false);

  useEffect(() => {
    // Immediate check
    const initial = calculateTimeLeft();
    if (initial.totalMs <= PRE_JOIN_WINDOW) setCanJoin(true);

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining.totalMs <= PRE_JOIN_WINDOW) {
        setCanJoin(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

const handleJoin = () => {
    if (onJoinClick) {
        onJoinClick(); // Run custom logic (API call + Navigation)
    }
    
    // Only open a new tab if a URL is explicitly provided. 
    // This allows the parent to handle navigation via Router if URL is empty.
    if (meetingUrl) {
        window.open(meetingUrl, '_blank');
    }
  };
  if (timeLeft.totalMs < -(2 * 60 * 60 * 1000)) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        className="w-full z-20 shadow-lg border-b border-[#5e35b1]"
        style={{
          background: 'linear-gradient(90deg, #7E57C2 0%, #9575cd 100%)',
          color: 'white',
        }}
      >
        <div className="flex items-center justify-between px-4 py-3 max-w-5xl mx-auto">
          
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-2 rounded-full w-10 h-10 flex items-center justify-center shadow-inner">
              <FontAwesomeIcon icon={faVideo} className="text-white text-sm" />
            </div>
            <div className="flex flex-col justify-center items-start">
              <span className="font-bold text-base leading-tight drop-shadow-sm">{title}</span>
              <span className="text-xs opacity-80 font-medium tracking-wide">
                Starts at {new Date(targetDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-end min-w-[180px]">
            {canJoin ? (
              <motion.button
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleJoin}
                className="bg-white text-[#7E57C2] text-sm font-bold px-6 py-2 rounded-full shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all duration-200 uppercase tracking-wider"
              >
                Join Now
              </motion.button>
            ) : (
               <div className="flex items-start gap-1 p-2 bg-black/10 rounded-lg shadow-inner border border-white/10">
                  {timeLeft.days > 0 && <FlipDigit value={timeLeft.days} label="DAYS" />}
                  <FlipDigit value={timeLeft.hours} label="HRS" />
                  <FlipDigit value={timeLeft.minutes} label="MIN" />
                  <FlipDigit value={timeLeft.seconds} label="SEC" />
               </div>
            )}
          </div>

        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MeetingBanner;